import { useState } from 'react';
import { Empty, ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatDate } from '../lib/format';
import { useAuthedImage, useResource } from '../lib/hooks';
import { ImageViewer } from '../components/ImageViewer';
import type { FeatureRequest, FeatureRequestPriority, FeatureRequestStatus } from '../lib/types';

/**
 * What people have asked the app to do — see migrations 168 and 169.
 *
 * ADMIN ONLY, under Settings, matching requireAdmin on the GET and the PUT.
 * Anyone signed in can raise one from the top bar of any page; this is where
 * they land, and where they are triaged.
 *
 * Opening a request is how you read it: the list shows the first lines of
 * something written in a textarea, and a request worth acting on is usually
 * longer than a row.
 */

const PRIORITIES: { value: FeatureRequestPriority; label: string; badge: string }[] = [
  { value: 'low', label: 'Low', badge: 'badge-neutral' },
  { value: 'medium', label: 'Medium', badge: 'badge-warning' },
  { value: 'high', label: 'High', badge: 'badge-serious' },
];

/* 'new' is where everything starts and is not something anybody sets on
   purpose — it is in the picker so a request can be put back to untouched. */
const STATUSES: { value: FeatureRequestStatus; label: string; badge: string }[] = [
  { value: 'new', label: 'New', badge: 'badge-neutral' },
  { value: 'in-progress', label: 'In progress', badge: 'badge-warning' },
  { value: 'testing', label: 'Testing', badge: 'badge-warning' },
  { value: 'completed', label: 'Completed', badge: 'badge-good' },
  /* Decided against — the work note says why (migration 197). */
  { value: 'not-implemented', label: 'Not implemented', badge: 'badge-neutral' },
];

const priorityOf = (v: FeatureRequestPriority | null) => PRIORITIES.find((p) => p.value === v);
const statusOf = (v: FeatureRequestStatus) =>
  STATUSES.find((s) => s.value === v) ?? { value: v, label: v, badge: 'badge-neutral' };

export function FeatureRequestsPage() {
  const data = useResource<{ requests: FeatureRequest[] }>('/feature-requests');
  const requests = data.data?.requests ?? [];
  const [open, setOpen] = useState<FeatureRequest | null>(null);

  const exportCsv = () =>
    downloadCsv(`feature_requests_${fileStamp()}.csv`, [
      ['Raised', 'Name', 'Page', 'Path', 'Request', 'By', 'Email', 'Priority', 'Status', 'What was done', 'Updated by', 'Updated'],
      ...requests.map((r) => [
        r.CreatedUtc,
        r.Name,
        r.PageLabel,
        r.PageUrl,
        r.Request,
        r.SubmittedByName,
        r.SubmittedByEmail,
        r.Priority,
        r.Status,
        r.WorkNote,
        r.UpdatedBy,
        r.UpdatedUtc,
      ]),
    ]);

  return (
    <>
      <PageHead
        title="Feature requests"
        lede="What people have asked the app to do, from the Feature request button in the top bar — with the page they were on when they asked. Open one to read it and set where it has got to."
      />

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading the feature requests" />}

      {data.data && (
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <h3>
                {requests.length} request{requests.length === 1 ? '' : 's'}
              </h3>
              <p className="sub">Newest first. Click one to open it.</p>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={requests.length === 0}
              onClick={exportCsv}
            >
              Export CSV
            </button>
          </div>
          {requests.length === 0 ? (
            <div className="card-body">
              <Empty title="Nothing asked for yet">
                <p>
                  The Feature request button is in the top bar of every page. What anyone sends
                  arrives here.
                </p>
              </Empty>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Raised</th>
                    <th style={{ width: '20%' }}>Name</th>
                    <th style={{ width: 150 }}>Page</th>
                    <th style={{ width: 90 }}>Priority</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 150 }}>By</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => {
                    const priority = priorityOf(r.Priority);
                    const status = statusOf(r.Status);
                    return (
                      <tr key={r.FeatureRequestId}>
                        <td className="xs" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(r.CreatedUtc)}
                        </td>
                        <td>
                          <button type="button" className="link-button" onClick={() => setOpen(r)}>
                            {r.Name}
                          </button>
                        </td>
                        <td className="xs">
                          {r.PageLabel ?? '—'}
                          <div className="subtle">{r.PageUrl}</div>
                        </td>
                        <td>
                          {priority ? (
                            <span className={`badge ${priority.badge}`}>{priority.label}</span>
                          ) : (
                            <span className="xs subtle">Not set</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${status.badge}`}>{status.label}</span>
                          {r.WorkNote && <div className="xs subtle">Has a note</div>}
                          {r.ImageIds.length > 0 && (
                            <div className="xs subtle">
                              {r.ImageIds.length} screenshot{r.ImageIds.length === 1 ? '' : 's'}
                            </div>
                          )}
                        </td>
                        <td className="xs">
                          {r.SubmittedByName ?? '—'}
                          {r.SubmittedByEmail && <div className="subtle">{r.SubmittedByEmail}</div>}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setOpen(r)}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {open && (
        <RequestDetail
          request={open}
          onClose={() => setOpen(null)}
          onSaved={() => {
            setOpen(null);
            void data.reload();
          }}
        />
      )}
    </>
  );
}

/** One request, in full, with its triage. */
function RequestDetail({
  request,
  onClose,
  onSaved,
}: {
  request: FeatureRequest;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [priority, setPriority] = useState<FeatureRequestPriority | ''>(request.Priority ?? '');
  const [status, setStatus] = useState<FeatureRequestStatus>(request.Status);
  const [workNote, setWorkNote] = useState(request.WorkNote ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    (request.Priority ?? '') !== priority ||
    request.Status !== status ||
    (request.WorkNote ?? '') !== workNote.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/feature-requests/${request.FeatureRequestId}`, {
        priority: priority === '' ? null : priority,
        status,
        workNote: workNote.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={request.Name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="xs muted">
        {request.PageLabel ? `${request.PageLabel} — ` : ''}
        {request.PageUrl} · raised {formatDate(request.CreatedUtc)}
        {request.SubmittedByName ? ` by ${request.SubmittedByName}` : ''}
      </p>

      {/* Written in a textarea, so it keeps the shape it was written in. */}
      <div className="am-notes-read mt-4">{request.Request}</div>

      {request.ImageIds.length > 0 && (
        <div className="mt-4">
          <p className="xs muted mb-2">
            {request.ImageIds.length} screenshot{request.ImageIds.length === 1 ? '' : 's'} — click one to see it full size.
          </p>
          <div className="shot-thumbs">
            {request.ImageIds.map((imageId, i) => (
              <RequestShot key={imageId} requestId={request.FeatureRequestId} imageId={imageId} index={i} />
            ))}
          </div>
        </div>
      )}

      <div className="form-grid mt-4">
        <Field label="Priority" hint="Empty until somebody decides it.">
          <select
            className="select"
            value={priority}
            onChange={(e) => setPriority(e.target.value as FeatureRequestPriority | '')}
          >
            <option value="">Not set</option>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as FeatureRequestStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="What I did" hint="What was changed or built, or why it was not — kept with the request.">
        <textarea
          className="textarea"
          rows={5}
          maxLength={4000}
          value={workNote}
          placeholder="e.g. Added a Refresh button to each dial on Service Delivery; deployed 30 Oct."
          onChange={(e) => setWorkNote(e.target.value)}
        />
      </Field>

      {request.UpdatedUtc && (
        <p className="xs subtle mt-2">
          Last moved {formatDate(request.UpdatedUtc)}
          {request.UpdatedBy ? ` by ${request.UpdatedBy}` : ''}.
        </p>
      )}
    </Modal>
  );
}

/** One screenshot on a request: a thumbnail that opens full size. Fetched with
 *  the signed-in user's token, like every image the API serves. */
function RequestShot({ requestId, imageId, index }: { requestId: number; imageId: number; index: number }) {
  const url = useAuthedImage(`/feature-requests/${requestId}/images/${imageId}`);
  const [open, setOpen] = useState(false);
  const alt = `Screenshot ${index + 1}`;
  return (
    <>
      <button type="button" className="shot-thumb" disabled={!url} onClick={() => setOpen(true)} title="See it full size">
        {url ? <img src={url} alt={alt} /> : <span className="xs muted">Loading…</span>}
      </button>
      {open && url && <ImageViewer src={url} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}
